import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Box, CircularProgress, DialogContent, DialogTitle, Tooltip } from '@mui/material';
import styled from 'styled-components';

import { ReactComponent as TabShape } from '@/assets/icons/tab.svg';
import { getInitiativeById } from '@/features/canvas/api';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';
import { needsTruncation, truncateText } from '@/features/canvas/utils/text-utils';

import { InitiativeBudgetTab } from './InitiativeBudgetTab';
import { InitiativeGeneralInfoTab } from './InitiativeGeneralInfoTab';

// Tab panel component
interface TabPanelProps {
  index: number;
  value: number;
  children?: React.ReactNode;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`initiative-tabpanel-${index}`}
      aria-labelledby={`initiative-tab-${index}`}
      {...other}
      style={{
        height: '100%',
        display: value === index ? 'flex' : 'none',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {value === index && <Box sx={{ height: '100%', overflow: 'auto' }}>{children}</Box>}
    </div>
  );
};

interface InitiativeDetailsDialogContentProps {
  dialogTitle?: string;
  onRelationshipsStatusChange?: (hasRelationships: boolean) => void;
}

export const InitiativeDetailsDialogContent = ({
  dialogTitle,
  onRelationshipsStatusChange,
}: InitiativeDetailsDialogContentProps) => {
  const {
    filteredNodes,
    selectedNodeId,
    selectedInitiativeId,
    isPublicView,
    popupVisible,
    viewMode,
    roadmapNodes,
  } = useEditorContext();
  const { t } = useTranslation();

  // Check if budget tab should be shown
  const shouldShowBudgetTab = popupVisible.some(field => field.type === 'budget');

  // Initialize activeTab based on view mode
  // If in budget view and budget tab is available, open budget tab (1)
  // Otherwise, open general info tab (0)
  const [activeTab, setActiveTab] = useState(() => {
    if (viewMode === 'budget' && shouldShowBudgetTab) {
      return 1; // Budget tab
    }
    return 0; // General info tab
  });

  const { data: initiative, isLoading: isInitiativeLoading } = useQuery(
    [getInitiativeById.name, selectedInitiativeId || selectedNodeId],
    () => {
      // If we have a selectedInitiativeId, use it directly (for cross-roadmap navigation)
      if (selectedInitiativeId) {
        return getInitiativeById({ id: selectedInitiativeId });
      }

      // Otherwise, fall back to the old node-based approach
      // First try filteredNodes
      const filteredNode = filteredNodes.find(n => Number(n.id) === Number(selectedNodeId));
      if (filteredNode?.initiatives?.id) {
        return getInitiativeById({ id: Number(filteredNode.initiatives.id) });
      }

      // Then try roadmapNodes
      const roadmapNode = roadmapNodes.find(n => Number(n.id) === Number(selectedNodeId));
      if (roadmapNode?.initiatives?.id) {
        return getInitiativeById({ id: Number(roadmapNode.initiatives.id) });
      }

      throw new Error(`No initiative found for node ${selectedNodeId}`);
    },
    {
      enabled: Boolean((selectedInitiativeId || selectedNodeId) && !isPublicView),
    }
  );

  // In public view, get initiative data from filteredNodes or roadmapNodes
  const publicViewInitiative = useMemo(() => {
    if (!isPublicView) return undefined;

    // If we have a selectedInitiativeId but this is public view, we can't make API calls
    // so we need to find it in the available node data
    if (selectedInitiativeId) {
      // First try filteredNodes
      const filteredNode = filteredNodes.find(n => n.initiatives?.id === selectedInitiativeId);
      if (filteredNode) {
        return filteredNode.initiatives;
      }

      // Then try roadmapNodes
      const roadmapNode = roadmapNodes.find(n => n.initiatives?.id === selectedInitiativeId);
      if (roadmapNode) {
        return roadmapNode.initiatives;
      }
    }

    // Fall back to selectedNodeId approach
    if (selectedNodeId) {
      // First try filteredNodes
      const filteredNode = filteredNodes.find(n => Number(n.id) === Number(selectedNodeId));
      if (filteredNode) {
        return filteredNode.initiatives;
      }

      // Then try roadmapNodes
      const roadmapNode = roadmapNodes.find(n => Number(n.id) === Number(selectedNodeId));
      if (roadmapNode) {
        return roadmapNode.initiatives;
      }
    }

    return undefined;
  }, [isPublicView, selectedInitiativeId, selectedNodeId, filteredNodes, roadmapNodes]);

  // Use API data when available, otherwise use public view data
  const initiativeData = isPublicView ? publicViewInitiative : initiative;

  const handleTabChange = (index: number) => {
    setActiveTab(index);
  };

  useEffect(() => {
    // If we're on the Budget tab (1) and it's no longer visible, switch to General Info (0)
    if (activeTab === 1 && !shouldShowBudgetTab) {
      setActiveTab(0);
    }
  }, [shouldShowBudgetTab, activeTab]);

  // Reset tab selection when dialog opens with a new node or initiative
  useEffect(() => {
    if (viewMode === 'budget' && shouldShowBudgetTab) {
      setActiveTab(1);
    } else {
      setActiveTab(0);
    }
  }, [selectedNodeId, selectedInitiativeId, viewMode, shouldShowBudgetTab]);

  if (isInitiativeLoading) {
    return (
      <LoaderWrapper>
        <StyledLoader />
      </LoaderWrapper>
    );
  }

  return (
    <>
      <TabsContainer>
        <TabButton
          $isActive={activeTab === 0}
          $tabIndex={0}
          onClick={() => handleTabChange(0)}
          type="button"
        >
          <TabShape />
          <span>{t('editor.initiative_detail.general_info')}</span>
        </TabButton>
        {shouldShowBudgetTab && (
          <TabButton
            $isActive={activeTab === 1}
            $tabIndex={1}
            onClick={() => handleTabChange(1)}
            type="button"
          >
            <TabShape />
            <span>{t('editor.initiative_detail.budget')}</span>
          </TabButton>
        )}
      </TabsContainer>

      <DialogHeader>
        {(dialogTitle || initiativeData?.title) &&
        needsTruncation(dialogTitle || initiativeData?.title || '', 200) ? (
          <Tooltip title={dialogTitle || initiativeData?.title} placement="top" arrow>
            <StyledDialogTitle>
              {truncateText(dialogTitle || initiativeData?.title || '', 200)}
            </StyledDialogTitle>
          </Tooltip>
        ) : (
          <StyledDialogTitle>{dialogTitle || initiativeData?.title}</StyledDialogTitle>
        )}
      </DialogHeader>

      <StyledDialogContent>
        <TabContentContainer>
          <TabPanel value={activeTab} index={0}>
            <InitiativeGeneralInfoTab
              initiative={initiativeData}
              onRelationshipsStatusChange={onRelationshipsStatusChange}
            />
          </TabPanel>

          {shouldShowBudgetTab && (
            <TabPanel value={activeTab} index={1}>
              {initiativeData?.id && <InitiativeBudgetTab initiativeId={initiativeData.id} />}
            </TabPanel>
          )}
        </TabContentContainer>
      </StyledDialogContent>
    </>
  );
};

const TabsContainer = styled.div`
  position: absolute;
  top: -40px;
  right: 35px;
  display: flex;
  align-items: flex-end;
  gap: 0; /* No gap for overlap effect */
  height: 42px;
`;

const TabButton = styled.button<{ $isActive: boolean; $tabIndex: number }>`
  position: relative;
  height: 40px;
  min-width: 160px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 1.4rem;
  font-family: inherit;
  transition: all 0.2s ease;

  /* Z-index for layering - active tab on top */
  z-index: ${({ $isActive, $tabIndex }) => ($isActive ? 10 : 5 - $tabIndex)};

  /* Increased overlap between tabs */
  ${({ $tabIndex }) =>
    $tabIndex > 0 &&
    `
    margin-left: -20px;
  `}

  /* SVG background */
  & > svg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;

    path {
      /* Active tab: white, Inactive tab: corePPTWhite */
      fill: ${({ theme, $isActive }) =>
        $isActive ? theme.palette.background.paper : theme.palette.brand.corePPTWhite};
      transition: fill 0.2s ease;
    }
  }

  /* Text styling - centered and same color for both states */
  & > span {
    position: relative;
    z-index: 2;
    color: ${({ theme }) => theme.palette.brand.textPrimary};
    font-size: 1.4rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding-bottom: 2px; /* Fine-tune vertical centering */
    transition: font-weight 0.2s ease;
  }

  /* Minimal hover effect */
  &:hover:not(:disabled) {
    ${({ $isActive }) =>
      !$isActive &&
      `
      z-index: 6; /* Bring forward slightly on hover */
    `}
  }

  &:focus {
    outline: none;
  }

  /* Ensure content doesn't get cut off */
  overflow: visible;
`;

const DialogHeader = styled.div`
  padding: 0 2rem;
  background: ${({ theme }) => theme.palette.background.paper};
`;

const StyledDialogTitle = styled(DialogTitle)`
  padding: 0;
  color: ${({ theme }) => theme.palette.brand.textPrimary};
  font-size: 2rem;
  font-weight: 600;
  overflow: hidden;
  word-wrap: break-word;
  max-width: 100%;
`;

const StyledDialogContent = styled(DialogContent)`
  color: ${({ theme }) => theme.palette.brand.textPrimary};
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const TabContentContainer = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const LoaderWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 1rem 0;
`;

const StyledLoader = styled(CircularProgress)`
  color: ${({ theme }) => theme.palette.brand.editorTabPrimary};
`;
